import { useState, useEffect, useRef } from 'react';
import { collection, query, where, onSnapshot, addDoc, deleteDoc, doc, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from '../firebase/firebase';
import { startOfDay, endOfDay } from 'date-fns';

// Helper functions for demo mode
const getDemoTasks = () => {
  const stored = localStorage.getItem('demoTasks');
  return stored ? JSON.parse(stored) : [];
};

const saveDemoTasks = (tasks) => {
  localStorage.setItem('demoTasks', JSON.stringify(tasks));
};

const filterTasksByDate = (tasks, date) => {
  const start = startOfDay(date).getTime();
  const end = endOfDay(date).getTime();
  return tasks.filter(task => {
    const taskDate = new Date(task.date).getTime();
    return taskDate >= start && taskDate <= end;
  });
};

export function useTasks(selectedDate, isDemoMode = false, userId = null) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const isDemoModeRef = useRef(isDemoMode);
  const userIdRef = useRef(userId);

  // Update refs when values change
  useEffect(() => {
    isDemoModeRef.current = isDemoMode;
    userIdRef.current = userId;
  }, [isDemoMode, userId]);

  useEffect(() => {
    if (!selectedDate) return;

    if (isDemoModeRef.current) {
      // Demo mode: use localStorage
      const loadTasks = () => {
        const allTasks = getDemoTasks();
        const filteredTasks = filterTasksByDate(allTasks, selectedDate);
        setTasks(filteredTasks);
        setLoading(false);
      };

      // Load initial tasks
      loadTasks();

      // Listen for storage changes
      const handleStorage = (e) => {
        if (e.key === 'demoTasks') {
          loadTasks();
        }
      };

      window.addEventListener('storage', handleStorage);
      return () => window.removeEventListener('storage', handleStorage);
    }

    // Firebase mode: use Firestore
    if (!userIdRef.current) {
      setTasks([]);
      setLoading(false);
      return;
    }

    const start = Timestamp.fromDate(startOfDay(selectedDate));
    const end = Timestamp.fromDate(endOfDay(selectedDate));

    const q = query(
      collection(db, 'tasks'),
      where('userId', '==', userIdRef.current),
      where('date', '>=', start),
      where('date', '<=', end)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const tasksData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setTasks(tasksData);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [selectedDate]);

  const addTask = async (text, date) => {
    try {
      if (isDemoModeRef.current) {
        const allTasks = getDemoTasks();
        const newTask = {
          id: Date.now().toString() + Math.random(),
          text,
          date: startOfDay(date).toISOString(),
          completed: false,
          createdAt: new Date().toISOString()
        };
        const updatedTasks = [...allTasks, newTask];
        saveDemoTasks(updatedTasks);
        
        // Manually update local state
        const filteredTasks = filterTasksByDate(updatedTasks, date);
        setTasks(filteredTasks);
      } else {
        if (!userIdRef.current) {
          console.error('No user ID available');
          return;
        }
        await addDoc(collection(db, 'tasks'), {
          text,
          date: Timestamp.fromDate(startOfDay(date)),
          completed: false,
          userId: userIdRef.current,
          createdAt: Timestamp.now()
        });
      }
    } catch (error) {
      console.error('Error adding task:', error);
    }
  };

  const removeTask = async (taskId) => {
    try {
      if (isDemoModeRef.current) {
        const allTasks = getDemoTasks();
        const updatedTasks = allTasks.filter(task => task.id !== taskId);
        saveDemoTasks(updatedTasks);
        
        // Manually update local state
        const filteredTasks = filterTasksByDate(updatedTasks, selectedDate);
        setTasks(filteredTasks);
      } else {
        await deleteDoc(doc(db, 'tasks', taskId));
      }
    } catch (error) {
      console.error('Error removing task:', error);
    }
  };

  const toggleTask = async (taskId, completed) => {
    try {
      if (isDemoModeRef.current) {
        const allTasks = getDemoTasks();
        const updatedTasks = allTasks.map(task =>
          task.id === taskId ? { ...task, completed: !completed } : task
        );
        saveDemoTasks(updatedTasks);
        
        // Manually update local state
        const filteredTasks = filterTasksByDate(updatedTasks, selectedDate);
        setTasks(filteredTasks);
      } else {
        await updateDoc(doc(db, 'tasks', taskId), {
          completed: !completed
        });
      }
    } catch (error) {
      console.error('Error toggling task:', error);
    }
  };

  return { tasks, loading, addTask, removeTask, toggleTask };
}

export function useTasksForPeriod(startDate, endDate, isDemoMode = false, userId = null) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const isDemoModeRef = useRef(isDemoMode);
  const userIdRef = useRef(userId);

  // Update refs when values change
  useEffect(() => {
    isDemoModeRef.current = isDemoMode;
    userIdRef.current = userId;
  }, [isDemoMode, userId]);

  useEffect(() => {
    if (!startDate || !endDate) return;

    if (isDemoModeRef.current) {
      // Demo mode: use localStorage
      const allTasks = getDemoTasks();
      const start = startOfDay(startDate).getTime();
      const end = endOfDay(endDate).getTime();
      const filteredTasks = allTasks.filter(task => {
        const taskDate = new Date(task.date).getTime();
        return taskDate >= start && taskDate <= end;
      });
      setTasks(filteredTasks);
      setLoading(false);
      return;
    }

    // Firebase mode: use Firestore
    if (!userIdRef.current) {
      setTasks([]);
      setLoading(false);
      return;
    }

    const start = Timestamp.fromDate(startOfDay(startDate));
    const end = Timestamp.fromDate(endOfDay(endDate));

    const q = query(
      collection(db, 'tasks'),
      where('userId', '==', userIdRef.current),
      where('date', '>=', start),
      where('date', '<=', end)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const tasksData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setTasks(tasksData);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [startDate, endDate]);

  return { tasks, loading };
}
